
import { Component, Emit, Prop, Vue } from 'vue-property-decorator';
import CreateEventForm from '@/active-components/dashboard/CreateEventForm.vue';
import { mapActions, mapGetters, mapMutations } from 'vuex';
import {
    createDuplicateEventRequestFromFormData,
    DuplicateEventRequest,
    DuplicateEventSource,
    EventFormData,
    EventResponse,
    EventUpdate,
    resolveEventProjectId,
} from '@/model/Event';
import { Project } from '@/model/Project';
import { ApiError } from '@/model/ApiError';

@Component({
    components: {
        CreateEventForm,
    },
    computed: {
        ...mapGetters({
            getProjects: 'customerProjectStorage/getProjects',
            eventError: 'eventStorage/getError',
        }),
    },
    methods: {
        ...mapMutations({
            setError: 'eventStorage/setError',
        }),
        ...mapActions({
            create: 'eventStorage/create',
            duplicateEvent: 'eventStorage/duplicateEvent',
            fetchDuplicateSource: 'eventStorage/fetchDuplicateSource',
            fetchEvents: 'eventStorage/fetchEvents',
            getAllProjects: 'customerProjectStorage/getAllProjects',
            update: 'eventStorage/updateEvent',
        }),
    },
})
export default class CreateEventModal extends Vue {
    @Prop({ type: String, default: 'create' }) mode!: string;

    @Prop({ default: null }) item!: EventResponse | null;

    loading = false;

    duplicateEventSource: DuplicateEventSource | null = null;

    create!: (formData: EventFormData) => Promise<void | EventResponse>;

    duplicateEvent!: (request: {
        projectId: number;
        eventRequest: DuplicateEventRequest;
    }) => Promise<void | EventResponse>;

    update!: (formData: EventUpdate) => Promise<void>;

    fetchDuplicateSource!: (
        eventId: number,
    ) => Promise<DuplicateEventSource | void>;

    setError!: (error: ApiError | null) => void;

    fetchEvents!: () => Promise<void | EventResponse[]>;

    getProjects!: Project[];

    eventError!: ApiError | null;

    getAllProjects!: (onlyActives: boolean) => Promise<void>;

    get isDuplicateMode(): boolean {
        return this.mode === 'duplicate';
    }

    get isUpdateMode(): boolean {
        return this.mode === 'update';
    }

    get currentItem(): EventResponse | null {
        return this.duplicateEventSource?.duplicatedEvent ?? this.item;
    }

    get initialStartDate(): Date | null {
        return (
            this.duplicateEventSource?.sourceStartDate ??
            this.item?.startDate ??
            null
        );
    }

    get initialProjectId(): number | null {
        return resolveEventProjectId(
            this.duplicateEventSource?.duplicatedEvent ?? this.item,
            this.getProjects,
        );
    }

    mounted(): void {
        this.loading = true;

        const loadingTasks = [this.getAllProjects(true)];

        if (this.isDuplicateMode && this.item?.id) {
            loadingTasks.push(
                this.fetchDuplicateSource(this.item.id).then((data) => {
                    this.duplicateEventSource = data ?? null;
                }),
            );
        }

        Promise.all(loadingTasks).finally(() => {
            this.loading = false;
        });
    }

    destroyed(): void {
        this.setError(null);
    }

    @Emit()
    close(): void {
        // close
    }

    @Emit()
    success(event?: EventResponse): EventResponse | undefined {
        return event;
    }

    onSubmit(formData: EventFormData | EventUpdate): void {
        this.loading = true;

        let method: Promise<void | EventResponse>;

        if (this.isUpdateMode) {
            method = this.update(formData as EventUpdate).then(() => undefined);
        } else if (this.isDuplicateMode) {
            if (!this.duplicateEventSource) {
                this.loading = false;
                return;
            }

            method = this.duplicateEvent({
                projectId: (formData as EventFormData).projectId as number,
                eventRequest: createDuplicateEventRequestFromFormData(
                    formData as EventFormData,
                    this.duplicateEventSource,
                ),
            });
        } else {
            method = this.create(formData as EventFormData);
        }

        method
            .then((createdEvent) => {
                this.fetchEvents();
                this.success(createdEvent);
            })
            .finally(() => {
                this.loading = false;
            });
    }
}
